import type { Config } from "./hooks/config";
// Straight to bootLoader, not the AppLoader index: the index pulls in React,
// which must stay out of this entry chunk (see the dynamic import below).
import { releaseBootLoader } from "./components/AppLoader/bootLoader";

const fallbackHtml = (title: string) => `
  <div style="font-family: system-ui, sans-serif; padding: 32px; max-width: 640px; margin: 64px auto; text-align: center;">
    <h2>${title}</h2>
    <p>Please refresh the page. If the problem persists, contact support.</p>
  </div>
`;

// Swaps the boot loader for an error page. The loader only leaves when told
// to, so every failed boot must come through here or it breathes forever.
function showBootFailure(container: HTMLElement, title: string, err: unknown) {
  container.innerHTML = fallbackHtml(title);
  releaseBootLoader();
  // eslint-disable-next-line no-console
  console.error("Failed to bootstrap app:", err);
}

async function bootstrap() {
  const container = document.getElementById("app");
  if (!container) return;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const res = await fetch("/api/config", {
      credentials: "same-origin",
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new Error(`Config load failed: ${res.status}`);
    }
    window.CONFIG = (await res.json()) as Config;
  } catch (err) {
    showBootFailure(container, "Failed to load configuration", err);
    return;
  } finally {
    window.clearTimeout(timeoutId);
  }

  // Dynamic import keeps react-dom/client and the entire app module graph
  // (including ~20 modules that read window.CONFIG at file scope) out of the
  // entry chunk. Otherwise the bundler emits a second physical copy of React
  // in the async vendor split, producing invalid-hook-call errors.
  try {
    const { mount } = await import("./mount");
    mount(container);
  } catch (err) {
    // e.g. a deploy replaced the app chunk under an open tab, or a module
    // failed to link.
    showBootFailure(container, "Failed to load the app", err);
  }
}

void bootstrap();
